import clsx from 'clsx'
import defer from 'lodash/defer'
import { useRouter } from 'next/router'
import React, { FC, useState } from 'react'
import useSWR from 'swr'

import { LoadingOverlay, LoadingSpinner } from '@Components/Loading'
import { PaginationNumberList } from '@Components/Pagination'
import { SearchResultsGridV2 } from '@Domains/Product/Features/SearchResults/Grid'
import SearchResultsHeader from '@Domains/Product/Features/SearchResults/Header'
import { algoliaFetcher } from '@Globals/Algolia/Fetcher'
import { Options, ProductSorting } from '@Globals/Algolia/Types'
import { useSplitTest } from '@Globals/SplitTests/Hooks'

import styles from './SearchResults.module.scss'

interface SearchResultsProps {
  id?: string
  optionalFilters?: string[]
  query: string
}

const SearchResults: FC<SearchResultsProps> = ({
  id,
  optionalFilters = [],
  query,
}) => {
  const { locale = '' } = useRouter()
  const sortingFeatureGroup = useSplitTest('sorting_feature_v1')
  const sortingGroup = useSplitTest('sorting_v1')

  const [options, setOptions] = useState<Options>({
    facets: [],
    facetFilters: [],
    filters: '',
    numericFilters: {},
    hitsPerPage: '30',
    locale,
    optionalFilters,
    page: 1,
    pagination: true,
    query,
    sorting: ProductSorting.Default,
    sortingFeatureGroup,
    sortingGroup,
  })

  const swr = useSWR(
    options,
    algoliaFetcher,
    {
      keepPreviousData: true,
      revalidateOnMount: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  )
  const hits = swr?.data?.hits || []
  const totalHits = swr?.data?.totalHits || 0
  const page = options.page || 1
  const pages = Math.ceil(totalHits / 30)

  return (
    <div className={clsx(styles['search-results'], styles.deptDesign)} id={id}>
      <SearchResultsHeader page={page} pages={pages} query={query} totalHits={totalHits} />

      <div className={styles['content-wrapper']}>
        {swr.isLoading && (
          <LoadingOverlay>
            <LoadingSpinner />
          </LoadingOverlay>
        )}

        <SearchResultsGridV2 hits={hits} />
      </div>

      {pages > 1 && (
        <PaginationNumberList
          changePage={(pageNumber: number) => {
            defer(() => {
              window.scrollTo({
                behavior: 'smooth',
                top: 0,
              })
            })

            setOptions(prev => ({
              ...prev,
              page: pageNumber,
            }))
          }}
          maxPageNumbers={7}
          page={page}
          pages={pages}
        />
      )}
    </div>
  )
}

export default SearchResults
